import { EditorNodeName } from "@/enums/customAttributes";
import { SERVICE_CUSTOM } from "@/helpers/constants";
import { BRACKET_COLORS } from "@/helpers/constants/attributeBuilder";
import { AttributeBuilderLexer } from "@/helpers/parser/lexer";
import { parserInstance } from "@/helpers/parser/parser";
import { FormulaVisitor } from "@/helpers/parser/visitor";
import { TokenTypeName } from "@/models/parser";

import type { EditorNode } from "@/models/TextEditor";
import type { FormulaOption } from "@/models/options";
import type { IAttribute, IContextSetValueModel } from "@/models/orchestration";
import type {
  BracketPair,
  ParseError,
  ParseResult,
  ParsedFormulaMetadata,
} from "@/models/parser";
import type { ILexingError, IRecognitionException, IToken } from "chevrotain";

export type { BracketPair, ParsedFormulaMetadata, ParseError, ParseResult };

interface AttributeLookupMaps {
  byName: Map<string, IAttribute>;
  byId: Map<string, IAttribute>;
  byNameLower: Map<string, IAttribute>;
  byIdLower: Map<string, IAttribute>;
}

const buildAttributeLookupMaps = (
  serviceAttributes: IAttribute[]
): AttributeLookupMaps => {
  const byName = new Map<string, IAttribute>();
  const byId = new Map<string, IAttribute>();
  const byNameLower = new Map<string, IAttribute>();
  const byIdLower = new Map<string, IAttribute>();

  for (const attr of serviceAttributes) {
    if (attr.name) {
      byName.set(attr.name, attr);
      byNameLower.set(attr.name.toLowerCase(), attr);
    }
    if (attr.id && attr.id !== SERVICE_CUSTOM) {
      byId.set(attr.id, attr);
      byIdLower.set(attr.id.toLowerCase(), attr);
    }
  }

  return { byName, byId, byNameLower, byIdLower };
};

export const stripColonPrefix = (name: string): string =>
  name.replace(/^:/, "");

interface FallbackContext {
  tokens: IToken[];
  functionsByNameMap: Record<string, FormulaOption>;
  serviceAttributes: IAttribute[];
  attributeLookup: AttributeLookupMaps;
  errors: ParseError[];
}

const createFallbackResult = (context: FallbackContext): ParseResult => {
  const {
    tokens,
    functionsByNameMap,
    serviceAttributes,
    attributeLookup,
    errors,
  } = context;

  const rawTokens = convertLexTokensToEditorNodes(
    tokens,
    functionsByNameMap,
    serviceAttributes,
    attributeLookup
  );
  const mergedMinus = mergeUnaryMinusForFallback(rawTokens);
  const fallbackTokens = mergeConsecutiveUnrecognized(mergedMinus);
  const bracketPairs = matchBracketsForFallback(fallbackTokens);

  return {
    tokens: fallbackTokens,
    metadata: {
      bracketPairs,
      functionCalls: [],
      totalTokens: fallbackTokens.length,
    },
    errors,
  };
};

const createSyntheticTokensFromLexerErrors = (
  input: string,
  tokens: IToken[],
  lexerErrors: ILexingError[]
): IToken[] => {
  if (!lexerErrors.length) {
    return tokens;
  }

  const syntheticTokens: IToken[] = lexerErrors.map((error) => ({
    image: input.slice(error.offset, error.offset + error.length),
    startOffset: error.offset,
    endOffset: error.offset + error.length - 1,
    startLine: error.line,
    endLine: error.line,
    startColumn: error.column ?? 1,
    endColumn: (error.column ?? 1) + error.length - 1,
    tokenTypeIdx: -1,
    tokenType: {
      name: TokenTypeName.Unrecognized,
      PATTERN: undefined,
    },
  }));

  const allTokens = [...tokens, ...syntheticTokens];
  allTokens.sort((a, b) => a.startOffset - b.startOffset);

  return allTokens;
};

export const parseFormula = (
  input: string,
  functionsByNameMap: Record<string, FormulaOption>,
  serviceAttributes: IAttribute[]
): ParseResult => {
  const attributeLookup = buildAttributeLookupMaps(serviceAttributes);
  const lexResult = AttributeBuilderLexer.tokenize(input);

  const errors: ParseError[] = [];

  if (lexResult.errors.length) {
    errors.push(
      ...lexResult.errors.map((err: ILexingError) => ({
        type: "lexer" as const,
        message: err.message,
        line: err.line,
        column: err.column,
        offset: err.offset,
        length: err.length,
      }))
    );
  }

  const tokensWithUnrecognized = createSyntheticTokensFromLexerErrors(
    input,
    lexResult.tokens,
    lexResult.errors
  );

  const hasWhitespace = tokensWithUnrecognized.some(
    (token) => token.tokenType?.name === TokenTypeName.WhiteSpace
  );

  if (hasWhitespace) {
    return createFallbackResult({
      tokens: tokensWithUnrecognized,
      functionsByNameMap,
      serviceAttributes,
      attributeLookup,
      errors,
    });
  }

  const tokensForParser = tokensWithUnrecognized.filter(
    (token) => token.tokenType?.name !== TokenTypeName.WhiteSpace
  );
  parserInstance.input = tokensForParser;
  const cst = parserInstance.expression();

  if (parserInstance.errors.length) {
    errors.push(
      ...parserInstance.errors.map((err: IRecognitionException) => ({
        type: "parser" as const,
        message: err.message,
        line: err.token?.startLine,
        column: err.token?.startColumn,
        offset: err.token?.startOffset,
        length:
          err.token?.endOffset !== undefined &&
          err.token?.startOffset !== undefined
            ? err.token.endOffset - err.token.startOffset + 1
            : undefined,
      }))
    );
  }

  if (!cst) {
    if (tokensWithUnrecognized.length) {
      return createFallbackResult({
        tokens: tokensWithUnrecognized,
        functionsByNameMap,
        serviceAttributes,
        attributeLookup,
        errors,
      });
    }

    return {
      tokens: [],
      metadata: {
        bracketPairs: new Map(),
        functionCalls: [],
        totalTokens: 0,
      },
      errors,
    };
  }

  const visitor = new FormulaVisitor(functionsByNameMap, serviceAttributes);
  const tokens = visitor.parse(cst);
  const metadata = visitor.getMetadata();

  const hasParserErrors = errors.some((e) => e.type === "parser");
  const visitorTokenCount = tokens.length;
  const allTokensCount = tokensWithUnrecognized.length;

  const shouldUseFallback =
    hasParserErrors && visitorTokenCount < allTokensCount;

  if (
    (visitorTokenCount === 0 || shouldUseFallback) &&
    errors.length &&
    allTokensCount > 0
  ) {
    return createFallbackResult({
      tokens: tokensWithUnrecognized,
      functionsByNameMap,
      serviceAttributes,
      attributeLookup,
      errors,
    });
  }

  return {
    tokens,
    metadata,
    errors,
  };
};

const tryMatchMultiWordAttribute = (
  tokens: IToken[],
  startIndex: number,
  serviceAttributes: IAttribute[]
): { attribute: IAttribute; tokensConsumed: number } | null => {
  const potentialParts: string[] = [];
  let tokensConsumed = 0;

  for (let tokenIndex = startIndex; tokenIndex < tokens.length; tokenIndex++) {
    const token = tokens[tokenIndex];
    const tokenName = token.tokenType?.name;

    const isIdentifier =
      tokenName === TokenTypeName.AttributeName ||
      tokenName === TokenTypeName.FunctionName;

    if (isIdentifier) {
      potentialParts.push(stripColonPrefix(token.image));
      tokensConsumed++;
    } else if (tokenName === TokenTypeName.WhiteSpace) {
      tokensConsumed++;
    } else {
      break;
    }

    if (potentialParts.length <= 1) {
      continue;
    }

    const combinedName = potentialParts.join(" ");
    const matchedAttribute = serviceAttributes.find(
      (attr) => attr.name?.toLowerCase() === combinedName.toLowerCase()
    );

    if (matchedAttribute) {
      return { attribute: matchedAttribute, tokensConsumed };
    }
  }

  return null;
};

interface NodeBuildOptions {
  uiId: string;
  attributeId?: string;
  context?: IContextSetValueModel;
  error?: { message: string };
  isOperand?: boolean;
}

interface TokenProcessingContext {
  tokens: IToken[];
  functionsByNameMap: Record<string, FormulaOption>;
  serviceAttributes: IAttribute[];
  attributeLookup: AttributeLookupMaps;
}

const NON_OPERAND_TOKEN_TYPES = new Set([
  TokenTypeName.LeftParenthesis,
  TokenTypeName.Comma,
  TokenTypeName.Plus,
  TokenTypeName.Minus,
  TokenTypeName.Star,
  TokenTypeName.Slash,
  TokenTypeName.Percent,
  TokenTypeName.Caret,
  TokenTypeName.Equals,
  TokenTypeName.NotEquals,
  TokenTypeName.LessThan,
  TokenTypeName.GreaterThan,
  TokenTypeName.LessEquals,
  TokenTypeName.GreaterEquals,
  TokenTypeName.LogicalAnd,
  TokenTypeName.LogicalOr,
]);

export const buildAttributeContext = (
  attribute: IAttribute
): IContextSetValueModel | undefined => {
  if (!attribute.context) {
    return undefined;
  }

  const contextValue =
    attribute.context.value ??
    (attribute.context.id ? { id: attribute.context.id } : undefined);

  return {
    type: attribute.context.type,
    value: contextValue,
  };
};

const processOperatorToken = (
  token: IToken,
  prevToken: IToken | undefined,
  nextToken: IToken | undefined,
  functionsByNameMap: Record<string, FormulaOption>,
  nodeOptions: NodeBuildOptions
): EditorNodeName | null => {
  const isFollowedByParen =
    nextToken?.tokenType?.name === TokenTypeName.LeftParenthesis;
  const isPrecededByOperand =
    prevToken &&
    !NON_OPERAND_TOKEN_TYPES.has(prevToken.tokenType?.name as TokenTypeName);

  if (!isFollowedByParen || isPrecededByOperand) {
    return null;
  }

  const upperName = token.image.toUpperCase();
  const isKnownFunction =
    upperName in functionsByNameMap || token.image in functionsByNameMap;

  if (isKnownFunction) {
    nodeOptions.isOperand = false;
    return EditorNodeName.function;
  }

  return null;
};

const processAttributeToken = (
  cleanName: string,
  attributeLookup: AttributeLookupMaps,
  nodeOptions: NodeBuildOptions
): { content: string } => {
  const attribute =
    attributeLookup.byName.get(cleanName) ||
    attributeLookup.byId.get(cleanName);

  if (!attribute) {
    return { content: cleanName };
  }

  nodeOptions.attributeId = attribute.id;
  if (attribute.id === SERVICE_CUSTOM && attribute.context) {
    nodeOptions.context = buildAttributeContext(attribute);
  }

  return { content: attribute.name };
};

const processUnrecognizedIdentifier = (
  tokens: IToken[],
  tokenIndex: number,
  nodeIndex: number,
  serviceAttributes: IAttribute[],
  nodeOptions: NodeBuildOptions
): { content: string; finalType: EditorNodeName; skipTokens: number } => {
  const multiWordMatch = tryMatchMultiWordAttribute(
    tokens,
    tokenIndex,
    serviceAttributes
  );

  if (!multiWordMatch) {
    return {
      content: "",
      finalType: EditorNodeName.unrecognized,
      skipTokens: 0,
    };
  }

  nodeOptions.attributeId = multiWordMatch.attribute.id;
  nodeOptions.uiId = `${nodeIndex}:${multiWordMatch.attribute.name}`;

  if (
    multiWordMatch.attribute.id === SERVICE_CUSTOM &&
    multiWordMatch.attribute.context
  ) {
    nodeOptions.context = multiWordMatch.attribute.context;
  }

  return {
    content: multiWordMatch.attribute.name,
    finalType: EditorNodeName.attribute,
    skipTokens: multiWordMatch.tokensConsumed - 1,
  };
};

const processBraceContext = (
  tokens: IToken[],
  tokenIndex: number,
  cleanName: string,
  nodeOptions: NodeBuildOptions,
  currentType: EditorNodeName
): { finalType: EditorNodeName; endIndex: number } => {
  const nextToken = tokens[tokenIndex + 1];
  if (nextToken?.tokenType?.name !== TokenTypeName.LeftBrace) {
    return { finalType: currentType, endIndex: tokenIndex };
  }

  const contextResult = extractAttributeContextFromTokens(
    tokens,
    tokenIndex + 1
  );
  if (!contextResult) {
    return { finalType: currentType, endIndex: tokenIndex };
  }

  try {
    const parsedContext = JSON.parse(
      contextResult.contextString
    ) as IContextSetValueModel;
    nodeOptions.context = parsedContext;

    if (cleanName === SERVICE_CUSTOM && parsedContext) {
      nodeOptions.attributeId = SERVICE_CUSTOM;
      delete nodeOptions.error;
      return {
        finalType: EditorNodeName.attribute,
        endIndex: contextResult.endIndex,
      };
    }
  } catch {
    // Invalid JSON, skip context
  }

  return { finalType: currentType, endIndex: contextResult.endIndex };
};

const processIdentifierToken = (
  token: IToken,
  tokens: IToken[],
  tokenIndex: number,
  nodeIndex: number,
  initialType: EditorNodeName,
  ctx: TokenProcessingContext,
  nodeOptions: NodeBuildOptions
): { content: string; finalType: EditorNodeName; newTokenIndex: number } => {
  const isFunctionToken = token.tokenType?.name === TokenTypeName.FunctionName;
  const cleanName = stripColonPrefix(token.image);
  let content = cleanName;
  let finalType = initialType;
  let newTokenIndex = tokenIndex;

  if (initialType === EditorNodeName.attribute) {
    const result = processAttributeToken(
      cleanName,
      ctx.attributeLookup,
      nodeOptions
    );
    content = result.content;
  } else if (initialType !== EditorNodeName.function) {
    const result = processUnrecognizedIdentifier(
      tokens,
      tokenIndex,
      nodeIndex,
      ctx.serviceAttributes,
      nodeOptions
    );
    if (result.skipTokens > 0) {
      content = result.content;
      finalType = result.finalType;
      newTokenIndex += result.skipTokens;
    }
  }

  const braceResult = processBraceContext(
    tokens,
    newTokenIndex,
    cleanName,
    nodeOptions,
    finalType
  );
  finalType = braceResult.finalType;
  newTokenIndex = braceResult.endIndex;

  if (finalType === EditorNodeName.unrecognized) {
    const errorType = isFunctionToken ? "function" : "attribute";
    nodeOptions.error = { message: `Unknown ${errorType}: ${content}` };
  }

  return { content, finalType, newTokenIndex };
};

const convertLexTokensToEditorNodes = (
  tokens: IToken[],
  functionsByNameMap: Record<string, FormulaOption>,
  serviceAttributes: IAttribute[],
  attributeLookup: AttributeLookupMaps
): EditorNode[] => {
  const { createEditorNode } = useEditorNodeFactory();
  const result: EditorNode[] = [];
  const ctx: TokenProcessingContext = {
    tokens,
    functionsByNameMap,
    serviceAttributes,
    attributeLookup,
  };

  let nodeIndex = 0;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const tokenTypeName = token.tokenType?.name;
    const nodeOptions: NodeBuildOptions = {
      uiId: `${nodeIndex}:${token.image}`,
    };

    if (tokenTypeName === TokenTypeName.WhiteSpace) {
      result.push(
        createEditorNode(token.image, EditorNodeName.whitespace, nodeOptions)
      );
      nodeIndex++;
      continue;
    }

    let content = token.image;
    let finalType = inferEditorNodeType(
      token,
      functionsByNameMap,
      attributeLookup
    );

    if (finalType === EditorNodeName.operator) {
      const newType = processOperatorToken(
        token,
        tokens[i - 1],
        tokens[i + 1],
        functionsByNameMap,
        nodeOptions
      );
      if (newType) {
        finalType = newType;
      }
    }

    const isIdentifierToken =
      tokenTypeName === TokenTypeName.AttributeName ||
      tokenTypeName === TokenTypeName.FunctionName;

    if (isIdentifierToken) {
      const result = processIdentifierToken(
        token,
        tokens,
        i,
        nodeIndex,
        finalType,
        ctx,
        nodeOptions
      );
      content = result.content;
      finalType = result.finalType;
      i = result.newTokenIndex;
    }

    result.push(createEditorNode(content, finalType, nodeOptions));
    nodeIndex++;
  }

  return result;
};

const extractAttributeContextFromTokens = (
  tokens: IToken[],
  startIndex: number
): { contextString: string; endIndex: number } | null => {
  if (tokens[startIndex]?.tokenType?.name !== TokenTypeName.LeftBrace) {
    return null;
  }

  let braceDepth = 0;
  const contextParts: string[] = [];

  for (let tokenIndex = startIndex; tokenIndex < tokens.length; tokenIndex++) {
    const token = tokens[tokenIndex];
    const tokenName = token.tokenType?.name;

    if (tokenName === TokenTypeName.LeftBrace) {
      braceDepth++;
      contextParts.push("{");
      continue;
    }

    if (tokenName === TokenTypeName.RightBrace) {
      braceDepth--;
      contextParts.push("}");

      if (braceDepth === 0) {
        return { contextString: contextParts.join(""), endIndex: tokenIndex };
      }
      continue;
    }

    contextParts.push(token.image);
  }

  return null;
};

const inferEditorNodeType = (
  token: IToken,
  functionsByNameMap: Record<string, FormulaOption>,
  attributeLookup: AttributeLookupMaps
): EditorNodeName => {
  const tokenType = token.tokenType?.name;

  switch (tokenType) {
    case TokenTypeName.InvalidIdentifier:
    case TokenTypeName.Unrecognized:
      return EditorNodeName.unrecognized;
    case TokenTypeName.NumberLiteral:
      return EditorNodeName.number;
    case TokenTypeName.StringLiteral:
    case TokenTypeName.IncompleteString:
      return EditorNodeName.regex;
    case TokenTypeName.LeftParenthesis:
    case TokenTypeName.RightParenthesis:
      return EditorNodeName.parenthesis;
    case TokenTypeName.Comma:
      return EditorNodeName.separator;
    case TokenTypeName.FunctionName: {
      const upperName = token.image.toUpperCase();
      if (upperName in functionsByNameMap) {
        return EditorNodeName.function;
      }
      const attrMatch =
        attributeLookup.byName.get(token.image) ||
        attributeLookup.byId.get(token.image);
      if (attrMatch) {
        return EditorNodeName.attribute;
      }
      return EditorNodeName.unrecognized;
    }
    case TokenTypeName.AttributeName: {
      const cleanName = stripColonPrefix(token.image);
      const cleanNameLower = cleanName.toLowerCase();
      const exactMatch =
        attributeLookup.byName.get(cleanName) ||
        attributeLookup.byId.get(cleanName);
      if (exactMatch) {
        const hasOtherPartialMatches = hasPartialMatchExcluding(
          attributeLookup,
          cleanNameLower,
          exactMatch
        );
        if (!hasOtherPartialMatches) {
          return EditorNodeName.attribute;
        }
      }
      const upperName = cleanName.toUpperCase();
      if (upperName in functionsByNameMap) {
        return EditorNodeName.function;
      }
      return EditorNodeName.unrecognized;
    }
    case TokenTypeName.Plus:
    case TokenTypeName.Minus:
    case TokenTypeName.Star:
    case TokenTypeName.Slash:
    case TokenTypeName.Percent:
    case TokenTypeName.Caret:
    case TokenTypeName.LessThan:
    case TokenTypeName.GreaterThan:
    case TokenTypeName.Equals:
    case TokenTypeName.NotEquals:
    case TokenTypeName.LessEquals:
    case TokenTypeName.GreaterEquals:
    case TokenTypeName.LogicalAnd:
    case TokenTypeName.LogicalOr:
      return EditorNodeName.operator;
    case TokenTypeName.WhiteSpace:
      return EditorNodeName.whitespace;
    default:
      return EditorNodeName.uncategorized;
  }
};

const hasPartialMatchExcluding = (
  lookup: AttributeLookupMaps,
  prefixLower: string,
  excludeAttr: IAttribute
): boolean => {
  for (const [key, attr] of lookup.byNameLower) {
    if (attr !== excludeAttr && key.startsWith(prefixLower)) {
      return true;
    }
  }
  for (const [key, attr] of lookup.byIdLower) {
    if (attr !== excludeAttr && key.startsWith(prefixLower)) {
      return true;
    }
  }
  return false;
};

const useEditorNodeFactory = () => {
  const createEditorNode = (
    content: string,
    type: EditorNodeName,
    additionalProps: Partial<EditorNode> = {}
  ): EditorNode => {
    const isAtomic = type === EditorNodeName.attribute;
    const isOperand = [
      EditorNodeName.attribute,
      EditorNodeName.regex,
      EditorNodeName.number,
      EditorNodeName.unrecognized,
      EditorNodeName.function,
    ].includes(type);

    const node: EditorNode = {
      id: `temp-${Math.random()}`,
      type,
      content,
      atomic: isAtomic,
      isOperand,
      isOperator:
        type === EditorNodeName.operator || type === EditorNodeName.function,
      error: {},
      ...additionalProps,
    };

    return node;
  };

  return { createEditorNode };
};

const mergeUnaryMinusForFallback = (tokens: EditorNode[]): EditorNode[] => {
  if (tokens.length < 2) {
    return tokens;
  }

  const result: EditorNode[] = [];
  const openingBrackets = ["(", "["];
  let i = 0;

  const findPrevNonWhitespace = (): EditorNode | undefined => {
    for (let j = result.length - 1; j >= 0; j--) {
      if (result[j].type !== EditorNodeName.whitespace) {
        return result[j];
      }
    }
    return undefined;
  };

  while (i < tokens.length) {
    const current = tokens[i];
    const next = tokens[i + 1];

    const isMinusOperator =
      current.type === EditorNodeName.operator && current.content === "-";
    const nextIsNumber = next?.type === EditorNodeName.number;

    if (isMinusOperator && nextIsNumber) {
      const prev = findPrevNonWhitespace();
      const isOpeningBracket =
        openingBrackets.includes(prev?.content ?? "") &&
        (prev?.type === EditorNodeName.parenthesis ||
          prev?.type === EditorNodeName.uncategorized);
      const isUnaryContext =
        !prev ||
        prev.type === EditorNodeName.operator ||
        prev.type === EditorNodeName.separator ||
        isOpeningBracket;

      if (isUnaryContext) {
        result.push({
          ...next,
          content: `-${next.content}`,
          uiId: `${result.length}:-${next.content}`,
        });
        i += 2;
        continue;
      }
    }

    result.push({ ...current, uiId: `${result.length}:${current.content}` });
    i++;
  }

  return result;
};

const mergeConsecutiveUnrecognized = (tokens: EditorNode[]): EditorNode[] => {
  if (tokens.length < 2) {
    return tokens;
  }

  const result: EditorNode[] = [];
  let i = 0;

  while (i < tokens.length) {
    const current = tokens[i];

    if (current.type !== EditorNodeName.unrecognized) {
      result.push({ ...current, uiId: `${result.length}:${current.content}` });
      i++;
      continue;
    }

    let mergedContent = current.content;
    let j = i + 1;

    while (j < tokens.length) {
      const next = tokens[j];

      if (next.type === EditorNodeName.whitespace) {
        const afterWhitespace = tokens[j + 1];
        if (afterWhitespace?.type === EditorNodeName.unrecognized) {
          mergedContent += next.content + afterWhitespace.content;
          j += 2;
          continue;
        }
        break;
      }

      if (next.type === EditorNodeName.unrecognized) {
        mergedContent += next.content;
        j++;
        continue;
      }

      break;
    }

    result.push({
      ...current,
      content: mergedContent,
      uiId: `${result.length}:${mergedContent}`,
    });
    i = j;
  }

  return result;
};

const handleOpeningBracket = (
  node: EditorNode,
  index: number,
  stack: { node: EditorNode; index: number; depth: number }[]
): void => {
  stack.push({ node, index, depth: stack.length });
};

const handleClosingBracket = (
  node: EditorNode,
  index: number,
  stack: { node: EditorNode; index: number; depth: number }[],
  bracketPairs: Map<string, BracketPair>
): void => {
  const opening = stack.pop();

  if (!opening) {
    node.context = { matchedBracketsId: undefined, color: "" };
    return;
  }

  const color = BRACKET_COLORS[opening.depth % BRACKET_COLORS.length];

  const pair: BracketPair = {
    openingId: opening.node.id ?? "",
    closingId: node.id ?? "",
    openingIndex: opening.index,
    closingIndex: index,
    depth: opening.depth,
    color,
  };

  bracketPairs.set(opening.node.id ?? "", pair);
  bracketPairs.set(node.id ?? "", pair);

  opening.node.context = {
    matchedBracketsId: node.id,
    matchedBracketsIndex: index,
    color,
  };

  node.context = {
    matchedBracketsId: opening.node.id,
    matchedBracketsIndex: opening.index,
    color,
  };
};

const matchBracketsForFallback = (
  tokens: EditorNode[]
): Map<string, BracketPair> => {
  const bracketPairs = new Map<string, BracketPair>();
  const stack: { node: EditorNode; index: number; depth: number }[] = [];

  tokens.forEach((node, index) => {
    if (node.type !== EditorNodeName.parenthesis) {
      return;
    }

    if (node.content === "(") {
      handleOpeningBracket(node, index, stack);
      return;
    }

    if (node.content === ")") {
      handleClosingBracket(node, index, stack, bracketPairs);
    }
  });

  for (const { node, index } of stack) {
    node.context = {
      matchedBracketsId: undefined,
      matchedBracketsIndex: index,
      color: "",
    };
  }

  return bracketPairs;
};

export const findBracketPair = (
  bracketId: string,
  metadata: ParsedFormulaMetadata
) => {
  return metadata.bracketPairs.get(bracketId);
};

export const findFunctionAtPosition = (
  position: number,
  metadata: ParsedFormulaMetadata
) => {
  return metadata.functionCalls.find(
    (fn) => position >= fn.startIndex && position <= fn.endIndex
  );
};

export const findArgumentAtPosition = (
  position: number,
  metadata: ParsedFormulaMetadata
) => {
  const functionCall = findFunctionAtPosition(position, metadata);
  if (!functionCall) {
    return null;
  }

  const argument = functionCall.argumentRanges.find(
    (arg) => position >= arg.startIndex && position <= arg.endIndex
  );

  return argument
    ? {
        ...argument,
        functionCall,
      }
    : null;
};

export const findActiveBracketPair = (
  cursorPosition: number,
  metadata: ParsedFormulaMetadata
) => {
  let nearestBracket: ReturnType<typeof findBracketPair> = undefined;
  let minDistance = Infinity;

  for (const pair of metadata.bracketPairs.values()) {
    const isOutsidePair =
      cursorPosition < pair.openingIndex || cursorPosition > pair.closingIndex;

    if (isOutsidePair) {
      continue;
    }

    const distance = cursorPosition - pair.openingIndex;

    if (distance >= minDistance) {
      continue;
    }

    minDistance = distance;
    nearestBracket = pair;
  }

  return nearestBracket;
};

export { AttributeBuilderLexer } from "@/helpers/parser/lexer";
export { parserInstance } from "@/helpers/parser/parser";
export { FormulaVisitor } from "@/helpers/parser/visitor";
